import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { useAuth } from "@/lib/AuthContext";
import { 
        LayoutDashboard, 
        Users, 
        FolderTree, 
        Package, 
        Settings as SettingsIcon,
        Webhook,
        Bell,
        Moon,
        Sun,
        Mail,
        Map,
        FlaskConical,
        Shield,
        BarChart3,
        Activity,
        Sparkles,
        FileText
      } from "lucide-react";
import { useEntorno } from "@/lib/EntornoContext";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Switch } from "@/components/ui/switch";
import { PanelLeftClose, PanelLeft } from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

export default function Layout({ children }) {
  const location = useLocation();
  const [darkMode, setDarkMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  const { user } = useAuth();
  const { isPruebas } = useEntorno();

  useEffect(() => {
    if (user) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const toggleTheme = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const allNavigationItems = [
    {
      title: "CEO Dashboard",
      url: createPageUrl("CEODashboard"),
      icon: Activity,
      visible: isAdmin
    },
    {
      title: "CRM Leads",
      url: createPageUrl("Dashboard"),
      icon: Users,
      visible: true
    },
    {
      title: "Presupuesto libre",
      url: createPageUrl("PresupuestoLibre"),
      icon: FileText,
      visible: true
    },
    {
      title: "Métricas",
      url: createPageUrl("Metrics"),
      icon: BarChart3,
      visible: isAdmin
    },
    {
      title: "Categorías",
      url: createPageUrl("Categorias"),
      icon: FolderTree,
      visible: true
    },
    {
      title: "Productos",
      url: createPageUrl("Productos"),
      icon: Package,
      visible: true
    },
    {
      title: "Casos IA",
      url: createPageUrl("Casos"),
      icon: Sparkles,
      visible: isAdmin
    },
    {
      title: "Webhooks",
      url: createPageUrl("Webhooks"),
      icon: Webhook,
      visible: isAdmin
    },
    {
          title: "Entorno de Pruebas",
          url: createPageUrl("EntornoPruebas"),
          icon: FlaskConical,
          visible: isAdmin
        },
        {
          title: "Alertas Discord",
          url: createPageUrl("Discord"),
          icon: Bell,
          visible: isAdmin
        },
        {
          title: "Config. Correo",
          url: createPageUrl("ConfiguracionCorreo"),
          icon: Mail,
          visible: isAdmin
        },
        {
          title: "Ajustes",
          url: createPageUrl("Ajustes"),
          icon: SettingsIcon,
          visible: isAdmin
        },
        {
          title: "Hoja de Ruta",
          url: createPageUrl("Roadmap"),
          icon: Map,
          visible: isAdmin
        },
        {
          title: "Usuarios",
          url: createPageUrl("Usuarios"),
          icon: Shield,
          visible: isAdmin
        },
      ];

  const navigationItems = allNavigationItems.filter(item => item.visible);

  return (
    <SidebarProvider defaultOpen={true}>
      <style>{`
        :root {
          --primary: 221 83% 53%;
          --primary-foreground: 210 40% 98%;
        }
        .dark {
          --background: 224 71% 4%;
          --foreground: 213 31% 91%;
          --card: 224 71% 4%;
          --card-foreground: 213 31% 91%;
          --popover: 224 71% 4%;
          --popover-foreground: 213 31% 91%;
          --muted: 223 47% 11%;
          --muted-foreground: 215.4 16.3% 56.9%;
          --border: 216 34% 17%;
          --input: 216 34% 17%;
        }
      `}</style>
      <div className="min-h-screen flex w-full bg-gray-50 dark:bg-gray-950 transition-colors duration-300">
        <Sidebar className="border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <SidebarHeader className="border-b border-gray-200 dark:border-gray-800 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">ER</span>
              </div>
              <div>
                <h2 className="font-bold text-gray-900 dark:text-white">Estudi Roig</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">Gestión de Presupuestos</p>
              </div>
            </div>
          </SidebarHeader>
          
          <SidebarContent className="p-2">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-2 py-2">
                Navegación
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navigationItems.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton 
                        asChild 
                        className={`hover:bg-indigo-50 dark:hover:bg-indigo-950 hover:text-indigo-700 dark:hover:text-indigo-300 transition-all duration-200 rounded-lg mb-1 ${
                          location.pathname === item.url ? 'bg-indigo-50 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        <Link to={item.url} className="flex items-center gap-3 px-3 py-2">
                          <item.icon className="w-4 h-4" />
                          <span className="font-medium">{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-gray-200 dark:border-gray-800 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                {darkMode ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                <span>Modo oscuro</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={darkMode} onCheckedChange={toggleTheme} />
                <SidebarTrigger className="hover:bg-gray-100 dark:hover:bg-gray-800 p-1.5 rounded-lg transition-colors duration-200" />
              </div>
            </div>
          </SidebarFooter>
        </Sidebar>

        <main className="flex-1 flex flex-col min-w-0">
          {/* Header Superior con Notificaciones */}
          <header className="h-16 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-40 flex items-center justify-between px-6 transition-colors duration-300">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="hover:bg-gray-100 dark:hover:bg-gray-800 p-1.5 rounded-lg transition-colors duration-200" />
              <div className="h-4 w-px bg-gray-200 dark:bg-gray-800 hidden md:block"></div>
              <span className="text-sm font-medium text-gray-500 dark:text-gray-400 hidden md:block">
                CRM Presupuestos
              </span>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBell />
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
            </div>
          </header>

          {/* Environment banner — only shows in test mode */}
          {isPruebas && (
            <div className="bg-orange-500 text-white text-center py-1.5 px-4 text-sm font-semibold flex items-center justify-center gap-2 z-50">
              <FlaskConical className="w-4 h-4" />
              MODO PRUEBAS ACTIVO — Los leads y correos que lleguen serán de test
            </div>
          )}
          <div className="flex-1 overflow-auto bg-gray-50 dark:bg-gray-950">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}